import { Link } from '@tanstack/react-router'
import React, { useRef } from 'react'
import { motion, useMotionValue, useSpring } from 'framer-motion'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'

interface NavItemProps {
    to: string
    icon: React.ElementType
    label: string
    color?: string
    orientation?: 'vertical' | 'horizontal'
    onClick?: () => void
}

export const NavItem: React.FC<NavItemProps> = ({ 
    to, 
    icon, 
    label, 
    orientation = 'vertical', 
    onClick 
}) => {
    const isVertical = orientation === 'vertical'
    
    // Magnetic Hover Physics
    const ref = useRef<HTMLAnchorElement>(null)
    const x = useMotionValue(0)
    const y = useMotionValue(0)
    const springConfig = { stiffness: 150, damping: 15, mass: 0.1 }
    const springX = useSpring(x, springConfig)
    const springY = useSpring(y, springConfig)

    const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (!ref.current) return
        const rect = ref.current.getBoundingClientRect()
        const centerX = rect.left + rect.width / 2
        const centerY = rect.top + rect.height / 2
        // Calculate distance from center, scale it down for subtle magnetic effect
        x.set((e.clientX - centerX) * 0.15)
        y.set((e.clientY - centerY) * 0.15)
    }

    const handleMouseLeave = () => {
        x.set(0)
        y.set(0)
    }

    const shortcut = `⇧${label.charAt(0).toUpperCase()}`

    return (
        <TooltipProvider>
            <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                    <Link
                        ref={ref}
                        to={to}
                        onClick={onClick}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={handleMouseLeave}
                        className="group relative flex items-center justify-center outline-none select-none shrink-0"
                        activeOptions={{ exact: to === '/' }}
                    >
                        {({ isActive }) => (
                            <motion.div 
                                style={{ x: springX, y: springY }}
                                className={`relative flex items-center justify-center gap-1.5 md:gap-2 px-3 py-1.5 md:px-3 md:py-1.5 lg:px-4 lg:py-2 rounded-full transition-colors duration-300
                                ${isActive 
                                    ? 'text-primary bg-transparent'
                                    : 'bg-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-slate-100'
                                }`}
                            >
                                {/* Animated Sliding Background */}
                                {isActive && (
                                    <motion.div
                                        layoutId="active-nav-indicator"
                                        className="absolute inset-0 bg-white dark:bg-slate-800 shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-none ring-1 ring-slate-200/60 dark:ring-slate-700/60 rounded-full z-0"
                                        initial={false}
                                        transition={{ 
                                            type: "spring", 
                                            stiffness: 450, 
                                            damping: 35,
                                            mass: 0.8
                                        }}
                                    />
                                )}

                                {/* Render Icon with micro-animation */}
                                <motion.div whileTap={{ scale: 0.8, y: 1 }} className="z-10">
                                    {React.createElement(icon, {
                                        className: `w-[16px] h-[16px] md:w-[18px] md:h-[18px] transition-transform duration-300 ${
                                            isActive 
                                            ? 'scale-[1.1]' 
                                            : `opacity-75 group-hover:opacity-100 group-hover:scale-110`
                                        }`,
                                        strokeWidth: isActive ? 2.5 : 2.0,
                                    })}
                                </motion.div>
                                
                                {/* Text Label */}
                                <span className={`text-[12px] md:text-[13px] tracking-tight capitalize whitespace-nowrap z-10 transition-all duration-300
                                    ${isVertical ? 'hidden lg:block' : 'hidden lg:block'}
                                    ${isActive 
                                        ? 'font-black' 
                                        : `font-bold opacity-80 group-hover:opacity-100`
                                    }`}
                                >
                                    {label}
                                </span>
                            </motion.div>
                        )}
                    </Link>
                </TooltipTrigger>
                <TooltipContent 
                    side="bottom" 
                    sideOffset={14} 
                    className="flex items-center gap-2.5 bg-slate-900/95 backdrop-blur-xl border-slate-800 text-white shadow-xl px-3 py-2 rounded-xl pointer-events-none"
                >
                    <span className="font-semibold text-xs">{label}</span>
                    <span className="px-1.5 py-0.5 bg-slate-800/80 rounded-md text-[9px] tracking-[0.2em] text-slate-300 font-black shadow-inner border border-slate-700/50">
                        {shortcut}
                    </span>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}
